import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useMap } from '../map/MapContext';
import { useOgcServices } from '../services/OgcServicesContext';
import { QUICK_PRESETS } from '../services/cql2-aviation-presets';
import type { FeatureEndpoints } from '../services/OgcServicesContext';
import type { FeatureCollection } from 'geojson';

// ─── Preset definitions ──────────────────────────────────────────────────────

interface PresetDef {
  id: string;
  label: string;
  description: string;
  collection: keyof FeatureEndpoints;
  buildCql: () => string;
}

const PRESETS: PresetDef[] = [
  { id: 'public-paved-3000',  label: 'Public Paved ≥3000 ft',  description: 'Public airports, paved runways, ≥3000 ft',     collection: 'airports',  buildCql: QUICK_PRESETS.publicPaved3000 },
  { id: 'towered-jet-fuel',   label: 'Towered + Jet Fuel',     description: 'Towered airports with JET-A fuel',              collection: 'airports',  buildCql: QUICK_PRESETS.toweredJetFuel },
  { id: 'vfr-light',          label: 'VFR Light Aircraft',      description: 'Public, ≥2000 ft, fuel available',              collection: 'airports',  buildCql: QUICK_PRESETS.vfrLightAircraft },
  { id: 'heliports',          label: 'Heliports',               description: 'Open heliports only',                           collection: 'airports',  buildCql: QUICK_PRESETS.heliports },
  { id: 'active-sua',         label: 'Active SUA',              description: 'MOA/Restricted/Prohibited active now',          collection: 'airspaces', buildCql: QUICK_PRESETS.activeSua },
  { id: 'active-tfrs',        label: 'Active TFRs',             description: 'Currently effective TFRs',                      collection: 'tfrs',      buildCql: QUICK_PRESETS.activeTfrs },
  { id: 'tall-obstacles',     label: 'Obstacles ≥500 ft',       description: 'Obstacles ≥500 ft AGL',                         collection: 'obstacles', buildCql: QUICK_PRESETS.tallObstacles },
  { id: 'very-tall',          label: 'Obstacles ≥1000 ft',      description: 'Obstacles ≥1000 ft AGL',                        collection: 'obstacles', buildCql: QUICK_PRESETS.veryTallObstacles },
  { id: 'high-navaids',       label: 'High Navaids',            description: 'High-altitude VOR/VORTAC/VOR-DME',              collection: 'navaids',   buildCql: QUICK_PRESETS.highNavaids },
  { id: 'terminal-navaids',   label: 'Terminal Navaids',        description: 'Terminal-class navaids',                         collection: 'navaids',   buildCql: QUICK_PRESETS.terminalNavaids },
];

// ─── Active filter state ─────────────────────────────────────────────────────

interface ActiveFilter {
  id: string;
  label: string;
  collection: keyof FeatureEndpoints;
  cql: string;
  resultCount: number | null;
  loading: boolean;
  error: string | null;
  sourceId: string;
}

export const CqlFilterPanel: React.FC = () => {
  const { map } = useMap();
  const { featuresClient } = useOgcServices();
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [customCql, setCustomCql] = useState('');
  const [customCollection, setCustomCollection] = useState<keyof FeatureEndpoints>('airports');
  const [showCustom, setShowCustom] = useState(false);
  const filterIdRef = useRef(0);

  // Apply a CQL filter: fetch results and add them as a GeoJSON source on the map
  const applyFilter = useCallback(async (
    label: string,
    collection: keyof FeatureEndpoints,
    cql: string,
  ) => {
    const id = `cql-filter-${filterIdRef.current++}`;
    const sourceId = `cql-src-${id}`;
    const filter: ActiveFilter = {
      id, label, collection, cql,
      resultCount: null, loading: true, error: null, sourceId,
    };
    setActiveFilters((prev) => [...prev, filter]);

    try {
      // Get current viewport for bbox intersection
      let bbox: [number, number, number, number] | undefined;
      if (map) {
        const bounds = map.getBounds();
        bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()];
      }

      const fc: FeatureCollection = bbox
        ? await featuresClient.searchByBboxWithFilter(collection, bbox, cql, 2000)
        : await featuresClient.getFeatures(collection, { filter: cql, limit: 2000 });

      // Add as GeoJSON source + layer on the map
      if (map) {
        if (map.getSource(sourceId)) {
          (map.getSource(sourceId) as any).setData(fc);
        } else {
          map.addSource(sourceId, { type: 'geojson', data: fc });

          // Auto-detect geometry type and add appropriate layer
          const geomType = fc.features[0]?.geometry?.type;
          if (geomType === 'Point' || geomType === 'MultiPoint') {
            map.addLayer({
              id: `${sourceId}-circle`,
              type: 'circle',
              source: sourceId,
              paint: {
                'circle-radius': 5,
                'circle-color': '#F59E0B',
                'circle-stroke-width': 1.5,
                'circle-stroke-color': '#0F172A',
              },
            });
            map.addLayer({
              id: `${sourceId}-label`,
              type: 'symbol',
              source: sourceId,
              layout: {
                'text-field': ['coalesce', ['get', 'ident'], ['get', 'name'], ''],
                'text-size': 10,
                'text-offset': [0, 1.4],
                'text-anchor': 'top',
                'text-font': ['Open Sans Regular'],
              },
              paint: {
                'text-color': '#F1F5F9',
                'text-halo-color': '#0F172A',
                'text-halo-width': 1,
              },
            });
          } else if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
            map.addLayer({
              id: `${sourceId}-fill`,
              type: 'fill',
              source: sourceId,
              paint: { 'fill-color': '#F59E0B', 'fill-opacity': 0.15 },
            });
            map.addLayer({
              id: `${sourceId}-line`,
              type: 'line',
              source: sourceId,
              paint: { 'line-color': '#F59E0B', 'line-width': 2 },
            });
          } else if (geomType === 'LineString' || geomType === 'MultiLineString') {
            map.addLayer({
              id: `${sourceId}-line`,
              type: 'line',
              source: sourceId,
              paint: { 'line-color': '#F59E0B', 'line-width': 2 },
            });
          }
        }
      }

      setActiveFilters((prev) => prev.map((f) =>
        f.id === id ? { ...f, loading: false, resultCount: fc.features.length } : f
      ));
    } catch (err: any) {
      setActiveFilters((prev) => prev.map((f) =>
        f.id === id ? { ...f, loading: false, error: err.message ?? 'Request failed' } : f
      ));
    }
  }, [map, featuresClient]);

  // Remove a filter and its map layers
  const removeFilter = useCallback((filterId: string) => {
    setActiveFilters((prev) => {
      const filter = prev.find((f) => f.id === filterId);
      if (filter && map) {
        // Remove all layers using this source
        for (const suffix of ['-circle', '-label', '-fill', '-line']) {
          try { map.removeLayer(`${filter.sourceId}${suffix}`); } catch { /* ok */ }
        }
        try { map.removeSource(filter.sourceId); } catch { /* ok */ }
      }
      return prev.filter((f) => f.id !== filterId);
    });
  }, [map]);

  const clearAllFilters = useCallback(() => {
    activeFilters.forEach((f) => removeFilter(f.id));
  }, [activeFilters, removeFilter]);

  const applyCustom = useCallback(() => {
    if (!customCql.trim()) return;
    applyFilter(`Custom: ${customCql.slice(0, 40)}…`, customCollection, customCql.trim());
  }, [customCql, customCollection, applyFilter]);

  const COLLECTION_OPTIONS: { key: keyof FeatureEndpoints; label: string }[] = [
    { key: 'airports', label: 'Airports' },
    { key: 'airspaces', label: 'Airspaces' },
    { key: 'navaids', label: 'Navaids' },
    { key: 'obstacles', label: 'Obstacles' },
    { key: 'fixes', label: 'Fixes' },
    { key: 'tfrs', label: 'TFRs' },
    { key: 'firBoundaries', label: 'FIR/UIR' },
  ];

  return (
    <div>
      {/* Quick presets */}
      <div className="panel-section">
        <div className="panel-section-title">Quick Filter Presets</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              className="list-item"
              style={{ cursor: 'pointer', border: 'none', background: 'transparent', textAlign: 'left', width: '100%' }}
              onClick={() => applyFilter(p.label, p.collection, p.buildCql())}
              title={`Collection: ${p.collection}\nCQL: ${p.buildCql()}`}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.description}</div>
              </div>
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{p.collection}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Custom CQL editor */}
      <div className="panel-section">
        <div
          className="collapsible-header"
          onClick={() => setShowCustom((p) => !p)}
        >
          <span className={`chevron ${showCustom ? 'open' : ''}`}>▶</span>
          <span className="panel-section-title" style={{ margin: 0 }}>Custom CQL2 Filter</span>
        </div>
        {showCustom && (
          <div style={{ marginTop: 6 }}>
            <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>Collection</label>
              <select
                className="input"
                value={customCollection}
                onChange={(e) => setCustomCollection(e.target.value as keyof FeatureEndpoints)}
                style={{ marginTop: 2 }}
              >
                {COLLECTION_OPTIONS.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: 6 }}>
              <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>CQL2-Text Expression</label>
              <textarea
                className="input"
                rows={4}
                placeholder={`e.g. facility_type = 'AIRPORT' AND longest_runway_ft >= 5000`}
                value={customCql}
                onChange={(e) => setCustomCql(e.target.value)}
                style={{ marginTop: 2, fontFamily: 'var(--font-mono)', fontSize: 12, resize: 'vertical' }}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={applyCustom}
              disabled={!customCql.trim()}
              style={{ width: '100%' }}
            >
              ▶ Apply CQL Filter
            </button>
            {/* CQL syntax help */}
            <details style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
              <summary style={{ cursor: 'pointer' }}>CQL2 Syntax Reference</summary>
              <div style={{ padding: '6px 0', lineHeight: 1.6, fontFamily: 'var(--font-mono)' }}>
                <div><b>Comparison:</b> =  &lt;&gt;  &gt;  &gt;=  &lt;  &lt;=</div>
                <div><b>Pattern:</b> LIKE  ILIKE  (% = wildcard)</div>
                <div><b>Set:</b> IN ('A','B','C')</div>
                <div><b>Range:</b> BETWEEN 100 AND 500</div>
                <div><b>Null:</b> IS NULL  IS NOT NULL</div>
                <div><b>Logic:</b> AND  OR  NOT</div>
                <div><b>Spatial:</b> S_INTERSECTS(geometry, POINT(lng lat))</div>
                <div><b>Temporal:</b> T_INTERSECTS(date, TIMESTAMP('...'))</div>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Active filters */}
      {activeFilters.length > 0 && (
        <div className="panel-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <div className="panel-section-title" style={{ margin: 0 }}>Active Filters ({activeFilters.length})</div>
            <button className="btn-icon" onClick={clearAllFilters} title="Clear all filters" style={{ fontSize: 12 }}>🗑️</button>
          </div>
          {activeFilters.map((f) => (
            <div key={f.id} className="list-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{f.label}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{f.collection}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {f.loading && <span style={{ fontSize: 11, color: 'var(--accent-light)' }}>Loading…</span>}
                  {f.resultCount != null && (
                    <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--green)' }}>
                      {f.resultCount} results
                    </span>
                  )}
                  {f.error && <span style={{ fontSize: 11, color: 'var(--red)' }} title={f.error}>Error</span>}
                  <button className="btn-icon" onClick={() => removeFilter(f.id)} title="Remove" style={{ fontSize: 12 }}>✕</button>
                </div>
              </div>
              <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', marginTop: 4, wordBreak: 'break-all' }}>
                {f.cql}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
